// Products / SKU + tồn kho — CRUD + Import Excel/CSV.

import React, { useState, useRef } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import * as XLSX from "xlsx"
import { toast } from "sonner"
import { Search, Plus, Boxes, UploadCloud, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, X } from "lucide-react"
import { get, post, patch, del, downloadCsv } from "@/lib/api"
import { money, num } from "@/lib/format"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog"
import { confirm } from "@/components/ui/confirm"
import { Segmented } from "@/components/ui/segmented"
import { IndexBadge } from "@/components/ui/data-table"
import { cn } from "@/lib/utils"
import { useUnitCan } from "@/lib/unitAccess"

/** Thẻ / dòng sản phẩm: mở trang sửa khi được quản lý sản phẩm, không thì chỉ hiện (nhân viên chỉ xem). */
function CardLink({ canManage, to, className, children }: { canManage: boolean; to: string; className?: string; children: React.ReactNode }) {
  return canManage ? <Link to={to} className={className}>{children}</Link> : <div className={className}>{children}</div>
}

interface Product {
  id: string
  sku: string
  name: string
  price: number
  cost: number
  stock: number
  unit: string
  weightGram: number
  lengthCm?: number | null
  widthCm?: number | null
  heightCm?: number | null
  active: boolean
  imageUrl?: string | null
}

type ProductView = "grid" | "list"
const VIEW_KEY = "shoptrack.products.view"

export default function ProductsPage() {
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  // Kiểu xem: thẻ (ảnh to, như sàn TMĐT) hoặc danh sách (mỗi sản phẩm một dòng) — nhớ trên máy này
  const [view, setView] = useState<ProductView>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid"
    } catch {
      return "grid"
    }
  })
  const changeView = (v: ProductView) => {
    setView(v)
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      /* chế độ riêng tư — chỉ không nhớ được */
    }
  }
  const [importOpen, setImportOpen] = useState(false)
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): thêm / sửa / ngừng bán / nhập file cần quyền quản lý sản phẩm
  const { can, seesMoney } = useUnitCan()
  const canManage = can("products.manage")

  const { data, isLoading } = useQuery({
    queryKey: ["products", q],
    queryFn: () => get<{ products: Product[] }>(`/products?q=${encodeURIComponent(q)}&active=all`),
    // Tồn kho đổi cả ở nơi khác (admin từ chối duyệt, hủy đơn, BEST hoàn) — mở lại là tải mới
    staleTime: 0,
  })

  const deactivateM = useMutation({
    mutationFn: (id: string) => del(`/products/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] })
      qc.invalidateQueries({ queryKey: ["products-active"] })
      toast.success("Đã ngừng bán")
    },
    onError: (err) => toast.error((err as Error).message),
  })

  // Bật lại sản phẩm đã ngừng bán (PATCH active=true) — hiện lại khi tạo đơn
  const reactivateM = useMutation({
    mutationFn: (p: Product) => patch(`/products/${p.id}`, { active: true }),
    onSuccess: (_r, p) => {
      qc.invalidateQueries({ queryKey: ["products"] })
      qc.invalidateQueries({ queryKey: ["products-active"] })
      qc.invalidateQueries({ queryKey: ["product", p.id] })
      toast.success(`Đã bật lại bán "${p.name}"`)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  async function onDeactivate(p: Product) {
    const ok = await confirm({
      title: `Ngừng bán ${p.name}?`,
      description: "Sản phẩm sẽ ẩn khỏi danh sách khi tạo đơn.",
      tone: "danger",
      confirmLabel: "Ngừng bán",
    })
    if (ok) deactivateM.mutate(p.id)
  }

  function downloadProductTemplate(type: "xlsx" | "csv" = "xlsx") {
    const headers = [
      {
        "Mã SKU": "SP-OXFORD-01",
        "Tên sản phẩm": "Áo sơ mi Oxford",
        "Giá bán": 350000,
        "Giá vốn": 150000,
        "Tồn kho": 120,
        "Đơn vị": "cái",
        "Khối lượng (gram)": 300,
        "Dài (cm)": 25,
        "Rộng (cm)": 15,
        "Cao (cm)": 5,
        "Ảnh sản phẩm (URL)": "https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=500",
      },
      {
        "Mã SKU": "SP-VAYHOA-02",
        "Tên sản phẩm": "Váy hoa nhí",
        "Giá bán": 420000,
        "Giá vốn": 180000,
        "Tồn kho": 85,
        "Đơn vị": "cái",
        "Khối lượng (gram)": 400,
        "Dài (cm)": 30,
        "Rộng (cm)": 20,
        "Cao (cm)": 5,
        "Ảnh sản phẩm (URL)": "",
      },
      {
        "Mã SKU": "SP-POLO-03",
        "Tên sản phẩm": "Áo thun Polo Classic",
        "Giá bán": 280000,
        "Giá vốn": 110000,
        "Tồn kho": 200,
        "Đơn vị": "cái",
        "Khối lượng (gram)": 250,
        "Dài (cm)": 20,
        "Rộng (cm)": 15,
        "Cao (cm)": 3,
        "Ảnh sản phẩm (URL)": "",
      },
    ]
    const ws = XLSX.utils.json_to_sheet(headers)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Mau_San_Pham")
    if (type === "xlsx") {
      XLSX.writeFile(wb, "Mau_nhap_san_pham_ShopTrack.xlsx")
    } else {
      XLSX.writeFile(wb, "Mau_nhap_san_pham_ShopTrack.csv")
    }
  }

  return (
    <div className="space-y-4 desktop:space-y-6">
      {/* Điện thoại: tiêu đề + nút cùng một hàng — Nhập file / Xuất CSV thành nút biểu tượng (có nhãn đọc màn hình) */}
      <div className="flex items-center justify-between gap-2" data-slot="page-head">
        <h1>Sản phẩm</h1>
        <div className="flex items-center gap-2">
          {/* Mẫu file chỉ cần trên máy tính (soạn Excel) */}
          {canManage && <Button variant="outline" size="sm" className="hidden desktop:inline-flex" onClick={() => downloadProductTemplate("xlsx")}>
             Tải mẫu
          </Button>}
          {canManage && <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} aria-label="Nhập Excel" title="Nhập Excel" className="mobile:w-11 mobile:px-0">
            <UploadCloud className="size-4" /><span className="mobile:hidden">Nhập Excel</span>
          </Button>}
          <Button variant="outline" size="sm" onClick={() => downloadCsv("/exports/products.csv")} aria-label="Xuất CSV" title="Xuất CSV" className="mobile:w-11 mobile:px-0">
            <Download className="size-4 desktop:hidden" /><span className="mobile:hidden">Xuất CSV</span>
          </Button>
          {canManage && <Link to="/products/new">
            <Button size="sm">
              <Plus className="size-4" />
              <span>
                Thêm<span className="mobile:hidden"> sản phẩm</span>
              </span>
            </Button>
          </Link>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted" />
          <Input placeholder="Tìm mã SKU hoặc tên…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" />
        </div>
        <Segmented
          className="ml-auto"
          label="Kiểu hiển thị sản phẩm"
          value={view}
          onChange={changeView}
          options={[
            ["grid", "Thẻ"],
            ["list", "Danh sách"],
          ]}
        />
      </div>

      {view === "grid" ? (
        <ProductGrid
          products={data?.products ?? []}
          loading={isLoading}
          onDeactivate={onDeactivate}
          onReactivate={(p) => reactivateM.mutate(p)}
          reactivating={reactivateM.isPending}
          canManage={canManage}
        />
      ) : (
        <ProductList
          products={data?.products ?? []}
          loading={isLoading}
          onDeactivate={onDeactivate}
          onReactivate={(p) => reactivateM.mutate(p)}
          reactivating={reactivateM.isPending}
          canManage={canManage}
          showCost={seesMoney}
        />
      )}


      {/* Product Import Modal */}
      {canManage && importOpen && (
        <ImportProductsModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ["products"] })
            qc.invalidateQueries({ queryKey: ["products-active"] })
          }}
        />
      )}
    </div>
  )
}

// ─── Nhập sản phẩm từ file ───────────────────────────────────────────

interface ImportRow {
  rowNum: number
  sku: string
  name: string
  price: number
  cost: number
  stock: number
  unit: string
  weightGram: number
  lengthCm: number | null
  widthCm: number | null
  heightCm: number | null
  imageUrl: string | null
  /** Lý do bỏ qua dòng (thiếu SKU, trùng SKU trong file, số không hợp lệ…) */
  error: string | null
}

/** Ô đầu tiên có giá trị trong các tên cột — giữ số 0 (|| sẽ coi 0 là trống). */
function pickCell(item: Record<string, any>, keys: string[]): unknown {
  for (const k of keys) {
    const v = item[k]
    if (v !== undefined && v !== null && String(v).trim() !== "") return v
  }
  return undefined
}

/** Số nguyên (tiền, tồn kho): "350.000", "350,000", "350000 ₫" → 350000. undefined = ô trống. */
function parseIntCell(v: unknown): number | undefined {
  if (v === undefined) return undefined
  if (typeof v === "number") return Number.isFinite(v) ? Math.round(v) : NaN
  const digits = String(v).replace(/[^\d-]/g, "")
  return digits ? Number(digits) : NaN
}

/** Số thập phân (kích thước, cân): "12,5" / "12.5" → 12.5. undefined = ô trống. */
function parseDecimalCell(v: unknown): number | undefined {
  if (v === undefined) return undefined
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN
  const t = String(v).trim().replace(",", ".").replace(/[^\d.]/g, "")
  return t ? Number(t) : NaN
}

/** Kích thước lưu theo cm nguyên: làm tròn, trống / 0 = không khai. */
function dimCm(v: unknown): number | null | typeof NaN {
  const n = parseDecimalCell(v)
  if (n === undefined || n === 0) return null
  if (Number.isNaN(n) || n < 0 || n > 1000) return NaN
  return Math.max(1, Math.round(n))
}

function ImportProductsModal({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)
  const [rows, setRows] = useState<ImportRow[]>([])
  const [fileName, setFileName] = useState<string | null>(null)

  // SKU đã có trong kho — dòng trùng sẽ CẬP NHẬT sản phẩm đó (báo trước trong bảng xem trước)
  const skuKey = rows.map((r) => r.sku).filter(Boolean).join("|")
  const existingSkus = useQuery({
    queryKey: ["products", "import-sku-check", skuKey],
    enabled: rows.length > 0,
    queryFn: async () => {
      const all = await get<{ products: Product[] }>("/products?active=all")
      const found = new Map(all.products.map((p) => [p.sku, p]))
      // API trả tối đa 200 sản phẩm: kho lớn hơn thì hỏi riêng các SKU còn lại
      if (all.products.length >= 200) {
        const rest = [...new Set(rows.map((r) => r.sku).filter((k) => k && !found.has(k)))].slice(0, 200)
        for (let i = 0; i < rest.length; i += 5) {
          const batch = await Promise.all(
            rest.slice(i, i + 5).map((k) => get<{ products: Product[] }>(`/products?active=all&q=${encodeURIComponent(k)}`).catch(() => null)),
          )
          for (const r of batch) for (const p of r?.products ?? []) found.set(p.sku, p)
        }
      }
      return found
    },
  })
  const existing = existingSkus.data

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        // CSV đọc dạng chữ UTF-8 (đọc nhị phân thì tiêu đề tiếng Việt như "Mã SKU" bị vỡ dấu → mọi dòng trống)
        const result = evt.target?.result
        const wb =
          typeof result === "string"
            ? XLSX.read(result, { type: "string" })
            : XLSX.read(new Uint8Array(result as ArrayBuffer), { type: "array" })
        const sheetName = wb.SheetNames[0]
        if (!sheetName) {
          toast.error("File không có trang tính nào")
          return
        }
        const ws = wb.Sheets[sheetName]
        if (!ws) {
          toast.error("Không thể đọc trang tính")
          return
        }
        const json: Record<string, any>[] = XLSX.utils.sheet_to_json(ws, { defval: "" })

        const firstRowBySku = new Map<string, number>()
        const parsed: ImportRow[] = json.map((item, idx) => {
          // Dòng 1 là tiêu đề → dữ liệu bắt đầu từ dòng 2 (khớp số dòng khi mở file)
          const rowNum = idx + 2
          const sku = String(pickCell(item, ["Mã SKU", "SKU", "sku"]) ?? "").trim()
          const name = String(pickCell(item, ["Tên sản phẩm", "Tên", "name"]) ?? "").trim()
          const price = parseIntCell(pickCell(item, ["Giá bán", "Giá", "price"])) ?? 0
          const cost = parseIntCell(pickCell(item, ["Giá vốn", "cost"])) ?? 0
          // Tồn kho trống = 0 (không tự đặt 100); ghi 0 thì đúng là 0
          const stock = parseIntCell(pickCell(item, ["Tồn kho", "Tồn", "stock"])) ?? 0
          const unit = String(pickCell(item, ["Đơn vị", "unit"]) ?? "cái").trim()
          const weightRaw = parseDecimalCell(pickCell(item, ["Khối lượng (gram)", "Cân nặng", "weight"]))
          const weightGram = weightRaw === undefined || weightRaw === 0 ? 300 : Math.round(weightRaw)
          const lengthCm = dimCm(pickCell(item, ["Dài (cm)", "Dài", "length"]))
          const widthCm = dimCm(pickCell(item, ["Rộng (cm)", "Rộng", "width"]))
          const heightCm = dimCm(pickCell(item, ["Cao (cm)", "Cao", "height"]))
          const imageUrl = String(pickCell(item, ["Ảnh sản phẩm (URL)", "Ảnh sản phẩm", "Ảnh", "imageUrl"]) ?? "").trim() || null

          let error: string | null = null
          if (!sku) error = "Thiếu mã SKU"
          else if (sku.length > 50) error = "Mã SKU dài quá 50 ký tự"
          else if (firstRowBySku.has(sku)) error = `Trùng SKU với dòng ${firstRowBySku.get(sku)}`
          else if (!name) error = "Thiếu tên sản phẩm"
          else if (Number.isNaN(price) || price < 0) error = "Giá bán không hợp lệ"
          else if (Number.isNaN(cost) || cost < 0) error = "Giá vốn không hợp lệ"
          else if (Number.isNaN(stock) || stock < 0 || stock > 1_000_000) error = "Tồn kho không hợp lệ"
          else if (Number.isNaN(weightGram) || weightGram < 1 || weightGram > 50_000) error = "Khối lượng phải từ 1 đến 50.000 gram"
          else if ([lengthCm, widthCm, heightCm].some((d) => Number.isNaN(d))) error = "Kích thước phải từ 1 đến 1.000 cm"
          if (sku && !firstRowBySku.has(sku)) firstRowBySku.set(sku, rowNum)

          return {
            rowNum,
            sku,
            name,
            price,
            cost,
            stock,
            unit: unit || "cái",
            weightGram,
            lengthCm,
            widthCm,
            heightCm,
            imageUrl,
            error,
          }
        })
        setRows(parsed)
        toast.success(`Đã đọc ${parsed.length} sản phẩm`)
      } catch (err) {
        toast.error("Lỗi khi đọc file: " + (err as Error).message)
      }
    }
    if (/\.csv$/i.test(file.name) || file.type === "text/csv") reader.readAsText(file, "utf-8")
    else reader.readAsArrayBuffer(file)
  }

  const validRows = rows.filter((r) => !r.error)
  const updateCount = existing ? validRows.filter((r) => existing.has(r.sku)).length : 0

  async function handleImport() {
    const valid = validRows
    if (!valid.length) {
      toast.error("Không có dòng dữ liệu hợp lệ")
      return
    }
    setImporting(true)
    try {
      const res = await post<any>("/products/batch-import", {
        products: valid.map((r) => ({
          sku: r.sku,
          name: r.name,
          price: r.price,
          cost: r.cost,
          stock: r.stock,
          unit: r.unit,
          weightGram: r.weightGram,
          lengthCm: r.lengthCm ?? null,
          widthCm: r.widthCm ?? null,
          heightCm: r.heightCm ?? null,
          imageUrl: r.imageUrl ?? null,
        })),
      })
      toast.success(`Đã nhập thành công ${res.imported}/${res.total} sản phẩm`)
      onSuccess()
      onClose()
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setImporting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl sm:max-w-3xl max-h-[85vh] flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Nhập Excel</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {!rows.length ? (
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-rule rounded-lg p-8 text-center hover:border-accent cursor-pointer bg-paperSoft/40"
            >
              <input type="file" ref={fileRef} onChange={handleFile} accept=".xlsx,.xls,.csv" className="hidden" />
              <UploadCloud className="size-10 mx-auto text-accent mb-2" />
              <p className="font-medium text-ink">Chọn file (.xlsx, .csv)</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg border border-rule bg-paperSoft">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-ink">{fileName} ({rows.length} dòng)</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setRows([]); setFileName(null) }} className="text-xs">
                  <X className="size-3.5 mr-1" /> Chọn lại
                </Button>
              </div>

              <p className="text-xs text-inkSoft">
                {existingSkus.isLoading ? (
                  "Đang đối chiếu SKU với kho…"
                ) : (
                  <>
                    <b className="text-ink">{validRows.length - updateCount}</b> thêm mới ·{" "}
                    <b className={cn(updateCount > 0 ? "text-warn" : "text-ink")}>{updateCount}</b> cập nhật sản phẩm có sẵn ·{" "}
                    <b className={cn(rows.length - validRows.length > 0 ? "text-danger" : "text-ink")}>{rows.length - validRows.length}</b> dòng bỏ qua
                  </>
                )}
              </p>

              <div className="border border-rule rounded-lg overflow-hidden max-h-60 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-paperSoft sticky top-0 border-b border-rule text-muted">
                    <tr>
                      <th className="p-2 text-left">Dòng</th>
                      <th className="p-2 text-left">SKU</th>
                      <th className="p-2 text-left">Tên sản phẩm</th>
                      <th className="p-2 text-right">Giá bán</th>
                      <th className="p-2 text-right">Giá vốn</th>
                      <th className="p-2 text-right">Tồn kho</th>
                      <th className="p-2 text-left">Kết quả</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-rule">
                    {rows.map((r) => {
                      const willUpdate = !r.error && existing?.has(r.sku)
                      return (
                        <tr key={r.rowNum} className={cn("hover:bg-paperSoft/50", r.error && "bg-danger/5", willUpdate && "bg-warn/5")}>
                          <td className="p-2 font-mono text-muted">{r.rowNum}</td>
                          <td className="p-2 font-mono font-medium">{r.sku || <span className="text-danger">—</span>}</td>
                          <td className="p-2 font-medium">{r.name}</td>
                          <td className="p-2 text-right font-mono">{money(r.price)}</td>
                          <td className="p-2 text-right font-mono text-muted">{money(r.cost)}</td>
                          <td className="p-2 text-right font-mono">{r.stock} {r.unit}</td>
                          <td className="p-2 min-w-36">
                            {r.error ? (
                              <span className="font-medium text-danger">Bỏ qua: {r.error}</span>
                            ) : willUpdate ? (
                              <span className="font-medium text-warn">
                                Cập nhật SP có sẵn
                              </span>
                            ) : (
                              <span className="text-ok">{existingSkus.isLoading ? "…" : "Thêm mới"}</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={importing}>Đóng</Button>
          {rows.length > 0 && (
            <Button onClick={handleImport} disabled={importing || validRows.length === 0 || existingSkus.isLoading}>
              {importing ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              {importing ? "Đang nhập…" : `Nhập ${validRows.length} sản phẩm`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Lưới thẻ sản phẩm kiểu sàn TMĐT (2 cột điện thoại → 5 cột màn rộng): ảnh vuông, tên 2 dòng, giá nổi
 * bật, SKU + tồn kho. Bấm thẻ để sửa; "Ngừng bán / Bật lại" ở chân thẻ.
 */
function ProductGrid({
  products,
  loading,
  onDeactivate,
  onReactivate,
  reactivating,
  canManage,
}: {
  products: Product[]
  loading: boolean
  onDeactivate: (p: Product) => void
  onReactivate: (p: Product) => void
  reactivating: boolean
  canManage: boolean
}) {
  if (loading) return <p className="py-10 text-center text-sm text-muted">Đang tải sản phẩm…</p>
  if (!products.length) {
    return (
      <div data-surface="solid" className="rounded-xl bg-raise px-6 py-12 text-center shadow-card">
        <p className="text-sm text-muted">Chưa có sản phẩm nào</p>
        {canManage && <Link to="/products/new" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">
          <Plus className="size-4" /> Thêm sản phẩm
        </Link>}
      </div>
    )
  }
  return (
    <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 sm:gap-3 lg:grid-cols-5 xl:grid-cols-6">
      {products.map((p, i) => {
        // Nhãn ngắn cho thẻ hẹp (3 cột trên điện thoại) — số còn lại đã có ở dòng "Kho N"
        const badge = !p.active
          ? { text: "Đã ngừng", cls: "bg-ink/70 text-white" }
          : p.stock <= 0
            ? { text: "Hết hàng", cls: "bg-danger text-white" }
            : p.stock <= 5
              ? { text: "Sắp hết", cls: "bg-warn text-white" }
              : null
        return (
          <li key={p.id} data-surface="solid" className="flex flex-col overflow-hidden rounded-xl bg-raise shadow-card">
            <CardLink
              canManage={canManage}
              to={`/products/${p.id}/edit`}
              className={cn("flex flex-1 flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50", !p.active && "opacity-70")}
            >
              <div className="relative aspect-square overflow-hidden bg-paperSoft">
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                    onError={(e) => ((e.target as HTMLElement).style.display = "none")}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted">
                    <Boxes className="size-6 opacity-30 sm:size-8" />
                  </div>
                )}
                {badge && <span className={cn("absolute left-1 top-1 rounded px-1 text-xs font-medium sm:left-1.5 sm:top-1.5 sm:px-1.5 sm:py-0.5", badge.cls)}>{badge.text}</span>}
                {/* STT ở góc phải ảnh — thẻ hẹp không đủ chỗ cạnh tên */}
                <IndexBadge n={i + 1} className="absolute right-1 top-1 sm:right-1.5 sm:top-1.5" />
              </div>
              <div className="flex flex-1 flex-col gap-0.5 p-2 sm:gap-1 sm:p-2.5">
                <p className="line-clamp-2 min-h-8 text-xs leading-4 text-ink sm:min-h-10 sm:text-sm sm:leading-5">{p.name}</p>
                <p className="mt-auto text-sm font-semibold tabular-nums text-accent sm:text-base">{money(p.price)}</p>
                <p className="flex items-center justify-between gap-2 text-xs text-muted">
                  {/* SKU chỉ trên màn rộng — thẻ hẹp giữ dòng kho (SKU có ở dạng Danh sách / trang sửa) */}
                  <span className="hidden truncate font-mono sm:inline">{p.sku}</span>
                  <span className="shrink-0">Kho {num(p.stock)}</span>
                </p>
              </div>
            </CardLink>
            {canManage && <div className="border-t border-rule/60">
              {p.active ? (
                <button
                  type="button"
                  onClick={() => onDeactivate(p)}
                  className="h-9 w-full cursor-pointer text-xs text-muted transition-colors hover:bg-danger/5 hover:text-danger"
                >
                  Ngừng bán
                </button>
              ) : (
                <button
                  type="button"
                  disabled={reactivating}
                  onClick={() => onReactivate(p)}
                  className="h-9 w-full cursor-pointer text-xs font-medium text-ok transition-colors hover:bg-ok/5 disabled:opacity-50"
                >
                  Bật lại bán
                </button>
              )}
            </div>}
          </li>
        )
      })}
    </ul>
  )
}

interface ProductViewProps {
  products: Product[]
  loading: boolean
  onDeactivate: (p: Product) => void
  onReactivate: (p: Product) => void
  reactivating: boolean
  canManage: boolean
  /** Giá vốn — nhân viên không có quyền xem tiền thì máy chủ không trả, ẩn luôn dòng */
  showCost: boolean
}

/** Dạng danh sách: mỗi sản phẩm một dòng — ảnh nhỏ, tên, SKU + tồn kho, giá; bấm dòng để sửa. */
function ProductList({ products, loading, onDeactivate, onReactivate, reactivating, canManage, showCost }: ProductViewProps) {
  if (loading) return <p className="py-10 text-center text-sm text-muted">Đang tải sản phẩm…</p>
  if (!products.length) return <ProductGrid products={[]} loading={false} onDeactivate={onDeactivate} onReactivate={onReactivate} reactivating={reactivating} canManage={canManage} />
  return (
    <ul data-surface="solid" className="divide-y divide-rule/60 overflow-hidden rounded-xl bg-raise shadow-card">
      {products.map((p, i) => {
        const stockCls = p.stock <= 0 ? "font-medium text-danger" : p.stock <= 5 ? "font-medium text-warn" : ""
        return (
          <li key={p.id} className={cn("flex items-center gap-2 pr-2", !p.active && "opacity-70")}>
            <CardLink
              canManage={canManage}
              to={`/products/${p.id}/edit`}
              className="flex min-h-14 min-w-0 flex-1 items-center gap-3 py-2 pl-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50"
            >
              {p.imageUrl ? (
                <img
                  src={p.imageUrl}
                  alt=""
                  loading="lazy"
                  className="size-10 shrink-0 rounded-lg object-cover ring-1 ring-rule"
                  onError={(e) => ((e.target as HTMLElement).style.display = "none")}
                />
              ) : (
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-paperSoft text-muted">
                  <Boxes className="size-4 opacity-40" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <IndexBadge n={i + 1} />
                  <span className="truncate text-sm font-medium text-ink">{p.name}</span>
                </span>
                <span className="block truncate text-xs text-muted">
                  <span className="font-mono">{p.sku}</span> ·{" "}
                  <span className={stockCls}>
                    {p.stock <= 0 ? "Hết hàng" : <>Kho {num(p.stock)}<span className="hidden desktop:inline"> {p.unit}</span></>}
                  </span>
                  {!p.active && " · Đã ngừng bán"}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-semibold tabular-nums text-accent">{money(p.price)}</span>
                {showCost && <span className="hidden text-xs tabular-nums text-muted desktop:block">Vốn {money(p.cost)}</span>}
              </span>
            </CardLink>
            {!canManage ? null : p.active ? (
              <button
                type="button"
                onClick={() => onDeactivate(p)}
                className="min-h-11 shrink-0 cursor-pointer rounded-md px-2 text-xs text-muted hover:bg-danger/5 hover:text-danger desktop:min-h-8"
              >
                Ngừng bán
              </button>
            ) : (
              <button
                type="button"
                disabled={reactivating}
                onClick={() => onReactivate(p)}
                className="min-h-11 shrink-0 cursor-pointer rounded-md px-2 text-xs font-medium text-ok hover:bg-ok/5 disabled:opacity-50 desktop:min-h-8"
              >
                Bật lại
              </button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
